// shared-types/src/serviceBilling/types.ts - Service billing (plans and charges for merchants)

/** Catalog price row and contract term: amount is the total for this period (e.g. full year for ANNUAL). */
export enum BillingInterval {
    MONTHLY = 'MONTHLY',
    SEMIANNUAL = 'SEMIANNUAL',
    ANNUAL = 'ANNUAL',
}

/** How often a subscription charge is generated (may split the contract total into installments). */
export enum PaymentFrequency {
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    SEMIANNUAL = 'SEMIANNUAL',
    ANNUAL = 'ANNUAL',
}

/** Single item in a spec category (display-like structure). */
export interface ServiceBillingPlanSpecItem {
    key: string
    label: string
}

/** Category for the plan comparison table (display only). */
export interface ServiceBillingPlanSpecCategory {
    id: string
    label: string
    /** Keys to show. Legacy when items is set. */
    itemKeys?: string[]
    /** Display-like: each item has key and label. When set, used instead of itemKeys + labels. */
    items?: ServiceBillingPlanSpecItem[]
}

/** Defines comparison table: structure and optional values. Stored in service_billing_plan.specs. */
export interface ServiceBillingPlanSpec {
    categories: ServiceBillingPlanSpecCategory[]
    /** Map key -> label. Used when category uses itemKeys (legacy). */
    labels?: Record<string, string>
    /** Values for this plan; when set, used for comparison instead of limits. */
    values?: Record<string, unknown>
}

export interface ServiceBillingPlanLimits {
    maxProducts?: number | null
    maxBranches?: number | null
    maxAccountUsers?: number | null
    maxProductCategories?: number | null
    maxDeliveryOptions?: number | null
    maxIntegrations?: number | null
    maxStorageMb?: number | null
    customDomainAllowed?: boolean
    bulkImportAllowed?: boolean
    maxBulkImportRows?: number | null
    advancedAnalytics?: boolean
    maxOrdersPerMonth?: number | null
    /** Soporte: "Estándar" | "Prioritario" | "Dedicado" */
    supportLevel?: string | null
    marketingIncluded?: boolean
    customApiIntegrations?: boolean
    storeCustomization?: boolean
    customEmail?: boolean
    /** Maximum number of mailboxes (email accounts) the account can create. */
    maxMailboxes?: number | null
    reportsAndExport?: boolean
    multiCurrency?: boolean
    webhooks?: boolean
    eInvoicing?: boolean
    trainingOnboarding?: boolean
    whiteLabel?: boolean
}

export interface ServiceBillingPlan {
    id: string
    name: string
    slug: string
    currency: string
    description?: string | null
    order: number
    /** Enforced and display values (maxProducts, maxBranches, feature flags, etc.). */
    limits?: ServiceBillingPlanLimits | null
    /** Optional: comparison table structure (categories, order, labels). If null, backoffice uses default. */
    specs?: ServiceBillingPlanSpec | null
    createdAt: Date
    updatedAt: Date
}

export interface ServiceBillingPlanPrice {
    id: string
    serviceBillingPlanId: string
    /** Contract / pricing period this row applies to (matches account assignment contract term). */
    billingInterval: BillingInterval
    amount: number
    aiImageCreditsIncluded: number
    aiTextCreditsIncluded: number
    createdAt: Date
    updatedAt: Date
}

export enum AccountServicePlanStatus {
    ACTIVE = 'ACTIVE',
    ENDED = 'ENDED',
    CANCELLED = 'CANCELLED',
}

export interface AccountServicePlan {
    id: string
    accountId: string
    serviceBillingPlanId: string
    status: AccountServicePlanStatus
    customAmount?: number | null
    /** Contract term; selects `service_billing_plan_price` row (catalog total for this term). */
    billingInterval?: BillingInterval | null
    /** Charge cadence; installments divide the contract catalog/custom total. Null = same as billingInterval (legacy). */
    paymentFrequency?: PaymentFrequency | null
    /** Optional one-time fee; generates an INSTALLATION charge when the job runs. */
    installationFee?: number | null
    /** First day of the first billable subscription period; installation fee is also anchored to this date. */
    startedAt: Date
    endedAt?: Date | null
    notes?: string | null
    createdAt: Date
    updatedAt: Date
    serviceBillingPlan?: ServiceBillingPlan
    prices?: ServiceBillingPlanPrice[]
}

export enum ChargeType {
    SUBSCRIPTION = 'SUBSCRIPTION',
    INSTALLATION = 'INSTALLATION',
    ADJUSTMENT = 'ADJUSTMENT',
    OTHER = 'OTHER',
}

export enum ChargeStatus {
    PENDING = 'PENDING',
    PAID = 'PAID',
    CANCELLED = 'CANCELLED',
}

/** Sociedad que recibió el pago del comercio (cobro en caja propia). No es el vendedor. */
export enum BillingSociety {
    COBRATICKET = 'COBRATICKET',
    RHINO = 'RHINO',
}

export interface AccountServiceBillingCharge {
    id: string
    accountId: string
    accountServicePlanId?: string | null
    amount: number
    currency: string
    type: ChargeType
    /** For SUBSCRIPTION: length of the billed period (payment frequency), e.g. MONTHLY or QUARTERLY. */
    billingInterval?: BillingInterval | PaymentFrequency | null
    periodStart?: Date | null
    periodEnd?: Date | null
    dueDate?: Date | null
    paidAt?: Date | null
    status: ChargeStatus
    externalReference?: string | null
    description?: string | null
    /** Public URL of invoice/receipt PDF or image (e.g. Gestión upload to GCS). */
    invoiceUrl?: string | null
    /** Set when status is PAID: which society collected the payment. */
    collectedBySociety?: BillingSociety | null
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}
